<script lang="ts">
  import { onMount, tick } from 'svelte';
  import TaskPanel from './TaskPanel.svelte';
  import TimerPanel from './TimerPanel.svelte';
  import InsightsPanel from './InsightsPanel.svelte';
  import PlanPanel from './PlanPanel.svelte';
  import PlanModal from './PlanModal.svelte';
  import SettingsModal from './SettingsModal.svelte';
  import { tasksStore } from '$lib/stores/tasks';
  import { plansStore } from '$lib/stores/plans';
  import { preferencesStore } from '$lib/stores/preferences';
  import { notificationStore } from '$lib/stores/notifications';
  import { historyStore } from '$lib/stores/history';
  import { syncEngine } from '$lib/stores/sync';
  import { aiStore } from '$lib/stores/ai';
  import type { SyncTable } from '$lib/db/sync';
  import { sendLocalNotification } from '$lib/services/notifications';
  import { consumeNotificationEvent } from '$lib/services/notificationEvents';
  import { derived } from 'svelte/store';
  import {
    getLayoutMode,
    initialPresentation,
    isModalOpen,
    type LayoutMode,
    type PresentationState,
  } from '$lib/domain/blueprint';
  import {
    createFocusDocument,
    saveFocusDocument,
    type SaveProgress,
  } from '$lib/services/focusDocument';
  import type { Plan } from '$lib/domain/types';

  type MobileTab = 'timer' | 'tasks' | 'plan' | 'insights';

  let presentation: PresentationState = initialPresentation;
  let editingPlan: Plan | null = null;
  let lastNotifiedPlan: string | null = null;
  let activeMobileTab: MobileTab = 'timer';
  let layoutMode: LayoutMode = 'desktop';
  let viewportWidth = 1280;
  let now = new Date();
  let isOnline = true;
  let initError = '';
  let syncLabel = 'Local first';
  let syncTone = 'ready';
  let saveStatus: SaveProgress = { phase: 'complete', progress: 1 };
  let saveLabel = 'Document saved';
  let documentReady = false;
  let documentSignature = '';
  let lastSavedSignature = '';
  let saveDebounce: number | null = null;
  let isFocusing = false;
  let workspaceRevealed = false;
  let focusStatus = 'Focus session ready.';

  $: syncLabel = !isOnline
    ? 'Offline'
    : $syncEngine.state === 'idle'
      ? $syncEngine.pending > 0
        ? `${$syncEngine.pending} pending`
        : 'All changes synced'
      : $syncEngine.state === 'syncing'
        ? 'Syncing'
        : $syncEngine.state === 'disabled'
          ? 'Local first'
          : 'Sync issue';
  $: syncTone =
    !isOnline || $syncEngine.state === 'error'
      ? 'issue'
      : $syncEngine.pending > 0
        ? 'pending'
        : 'ready';
  $: focusStatus = isFocusing
    ? workspaceRevealed
      ? 'Focus session running. Workspace controls available.'
      : 'Focus mode active.'
    : 'Workspace controls available.';
  $: layoutMode = getLayoutMode(viewportWidth);
  $: saveLabel =
    saveStatus.phase === 'serializing'
      ? 'Preparing document'
      : saveStatus.phase === 'writing'
        ? 'Saving document'
        : saveStatus.phase === 'error'
          ? 'Save issue'
          : 'Document saved';
  $: {
    if (presentation.kind === 'edit-plan-block') {
      const blockId = presentation.blockId;
      editingPlan = $plansStore.plans.find((plan) => plan.id === blockId) ?? null;
    } else {
      editingPlan = null;
    }
  }
  $: documentSignature = JSON.stringify({
    tasks: $tasksStore,
    plans: $plansStore.plans,
    sessions: $plansStore.sessions,
    history: $historyStore,
    ai: $aiStore.cards,
  });
  $: if (documentReady && documentSignature !== lastSavedSignature) {
    queueDocumentSave();
  }

  const themeStore = derived(preferencesStore, ($prefs) => $prefs.theme);

  const revealSection = (section: MobileTab) => {
    activeMobileTab = section;
    workspaceRevealed = section !== 'timer';
  };

  const navigateToSection = async (section: MobileTab) => {
    revealSection(section);
    await tick();
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const handleFocusChange = (event: CustomEvent<{ active: boolean }>) => {
    isFocusing = event.detail.active;
    workspaceRevealed = false;
    if (isFocusing) activeMobileTab = 'timer';
  };

  const openNewPlan = () => {
    presentation = { kind: 'new-document', source: 'plan' };
  };

  const openPlanEditor = (plan: Plan) => {
    presentation = { kind: 'edit-plan-block', blockId: plan.id };
  };

  const openSettings = () => {
    presentation = { kind: 'settings' };
  };

  const closePlanModal = () => {
    presentation = initialPresentation;
  };

  const closePresentation = () => {
    presentation = initialPresentation;
  };

  const queueDocumentSave = () => {
    if (!documentReady || documentSignature === lastSavedSignature) return;
    if (saveDebounce) window.clearTimeout(saveDebounce);
    saveDebounce = window.setTimeout(async () => {
      saveDebounce = null;
      const signatureAtSave = documentSignature;
      const doc = createFocusDocument({
        id: 'default',
        title: 'Focus Flow',
        tasks: $tasksStore,
        planBlocks: $plansStore.plans,
        sessions: $plansStore.sessions,
        history: $historyStore,
        aiInsights: $aiStore.cards,
      });

      try {
        await saveFocusDocument(doc, (progress) => {
          saveStatus = progress;
        });
        lastSavedSignature = signatureAtSave;
      } catch {
        saveStatus = { phase: 'error', progress: 1 };
      }
    }, 450);
  };

  const init = async () => {
    try {
      await Promise.all([
        tasksStore.load(),
        plansStore.load(),
        preferencesStore.load(),
        notificationStore.load(),
        historyStore.load(),
      ]);
      await syncEngine.sync();
      documentReady = true;
    } catch (error) {
      initError = error instanceof Error ? error.message : 'Failed to load local data.';
    }
  };

  const refreshPulledStores = async (tables: readonly SyncTable[]) => {
    const refreshes: Promise<void>[] = [];
    if (tables.includes('tasks')) refreshes.push(tasksStore.load());
    if (tables.includes('plans')) refreshes.push(plansStore.refreshFromSync());
    if (tables.includes('history') || tables.includes('plan_sessions'))
      refreshes.push(historyStore.load());
    if (tables.includes('preferences')) refreshes.push(preferencesStore.load());
    if (tables.includes('notification_settings')) refreshes.push(notificationStore.load());
    await Promise.all(refreshes);
  };

  onMount(() => {
    const stopRefreshingPulledStores = syncEngine.onPullApplied(refreshPulledStores);
    isOnline = navigator.onLine;
    viewportWidth = window.innerWidth;
    init();
    const updateOnline = () => {
      isOnline = navigator.onLine;
      if (isOnline) void syncEngine.sync();
    };
    const updateViewport = () => {
      viewportWidth = window.innerWidth;
    };
    window.addEventListener('online', updateOnline);
    window.addEventListener('offline', updateOnline);
    window.addEventListener('resize', updateViewport);
    const checkDuePlans = async () => {
      if (!$plansStore.loaded) return;
      const duePlan = await plansStore.checkForDuePlans();
      if (
        duePlan &&
        duePlan.id !== lastNotifiedPlan &&
        consumeNotificationEvent({
          eventId: `plan:${duePlan.id}:due`,
          scheduledAtMs: Date.parse(duePlan.startAt),
          recovered: false,
        })
      ) {
        lastNotifiedPlan = duePlan.id;
        if (
          notificationStore.isLoaded() &&
          $notificationStore.enabled &&
          $notificationStore.playOn.planStart
        ) {
          void sendLocalNotification(duePlan.autoStart ? 'Plan started' : 'Plan ready', {
            body: duePlan.autoStart
              ? 'Your plan auto-started.'
              : 'Your scheduled plan is ready to begin.',
            tag: `plan-${duePlan.id}`,
          });
        }
      }
    };
    let backgroundJobs: number[] = [];
    const updateVisibility = () => {
      backgroundJobs.forEach(clearInterval);
      backgroundJobs = [];
      document.documentElement.dataset.pageHidden = String(document.visibilityState === 'hidden');
      if (document.visibilityState === 'hidden') return;
      now = new Date();
      if (documentReady) void syncEngine.sync();
      void checkDuePlans();
      backgroundJobs = [
        window.setInterval(() => syncEngine.sync(), 120000),
        window.setInterval(() => {
          now = new Date();
        }, 60000),
        window.setInterval(() => {
          void checkDuePlans();
        }, 15000),
      ];
    };
    document.addEventListener('visibilitychange', updateVisibility);
    updateVisibility();
    const cleanupTheme = themeStore.subscribe((theme) => {
      document.documentElement.dataset.theme = theme;
    });

    return () => {
      stopRefreshingPulledStores();
      backgroundJobs.forEach(clearInterval);
      document.removeEventListener('visibilitychange', updateVisibility);
      delete document.documentElement.dataset.pageHidden;
      window.removeEventListener('online', updateOnline);
      window.removeEventListener('offline', updateOnline);
      window.removeEventListener('resize', updateViewport);
      if (saveDebounce) window.clearTimeout(saveDebounce);
      cleanupTheme();
    };
  });
</script>

<div
  class="app-shell"
  class:is-focusing={isFocusing && !workspaceRevealed}
  data-active-tab={activeMobileTab}
  data-layout-mode={layoutMode}
  data-modal-open={isModalOpen(presentation)}
>
  <header class="app-shell__header" data-testid="header">
    <a
      class="brand"
      href="#timer-tabpanel"
      on:click|preventDefault={() => navigateToSection('timer')}
      aria-label="Focus Flow home"
    >
      <span class="brand-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24"><path d="M4 9c3-3 5-3 8 0s5 3 8 0M4 15c3-3 5-3 8 0s5 3 8 0" /></svg
        >
      </span>
      <h1 class="brand__title">Focus Flow</h1>
    </a>
    <nav class="workspace-nav__links" aria-label="Workspace sections">
      <a
        class:active={activeMobileTab === 'timer'}
        aria-current={activeMobileTab === 'timer' ? 'location' : undefined}
        href="#timer-tabpanel"
        on:click|preventDefault={() => navigateToSection('timer')}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true"
          ><circle cx="12" cy="12" r="8" /><path d="M12 7v5l3 2" /></svg
        >
        <span>Today</span>
      </a>
      <a
        class:active={activeMobileTab === 'tasks'}
        aria-current={activeMobileTab === 'tasks' ? 'location' : undefined}
        href="#tasks-tabpanel"
        on:click|preventDefault={() => navigateToSection('tasks')}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true"
          ><path d="M8 6h12M8 12h12M8 18h12" /><path
            d="m3.8 6 1 1 2-2M3.8 12l1 1 2-2M3.8 18l1 1 2-2"
          /></svg
        >
        <span>Tasks</span>
      </a>
      <a
        class:active={activeMobileTab === 'plan'}
        aria-current={activeMobileTab === 'plan' ? 'location' : undefined}
        href="#plan-tabpanel"
        on:click|preventDefault={() => navigateToSection('plan')}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true"
          ><path
            d="M7 3v3M17 3v3M4.5 9.5h15M6.5 5.5h11a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-11a2 2 0 0 1-2-2v-10a2 2 0 0 1 2-2Z"
          /></svg
        >
        <span>Planner</span>
      </a>
      <a
        class:active={activeMobileTab === 'insights'}
        aria-current={activeMobileTab === 'insights' ? 'location' : undefined}
        href="#insights-tabpanel"
        on:click|preventDefault={() => navigateToSection('insights')}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true"
          ><path d="M5 16 10 11l3 3 6-7" /><path d="M15 7h4v4" /></svg
        >
        <span>Insights</span>
      </a>
    </nav>

    <button
      class="workspace-nav__settings"
      type="button"
      aria-label="Open Control Center"
      aria-haspopup="dialog"
      on:click={openSettings}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true"
        ><path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0-1 6.85" /><path
          d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 1.55V21a2 2 0 0 1-4 0v-.09a1.7 1.7 0 0 0-1-1.55 1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.55-1H3a2 2 0 0 1 0-4h.09a1.7 1.7 0 0 0 1.55-1 1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.55V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1 1.55 1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.22.63.82 1 1.55 1H21a2 2 0 1 1 0 4h-.09c-.73 0-1.33.37-1.55 1Z"
        /></svg
      >
      <span>Control Center</span>
    </button>
  </header>

  {#if initError}
    <div class="notice app-error" role="alert">{initError}</div>
  {/if}
  {#if $syncEngine.error || saveStatus.phase === 'error'}
    <div class="workspace-issue" role="status">
      <span>{$syncEngine.error || 'Your document could not be saved.'}</span>
      {#if $syncEngine.error}<button class="text-button" on:click={() => syncEngine.retry()}
          >Retry sync</button
        >{/if}
    </div>
  {/if}
  <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">{focusStatus}</div>

  <main class="focus-workspace" aria-label="Focus workspace">
    <TimerPanel
      compact={activeMobileTab !== 'timer'}
      on:openPlan={openNewPlan}
      on:focusChange={handleFocusChange}
      on:chooseTask={async () => {
        await navigateToSection('tasks');
        document.querySelector<HTMLInputElement>('[data-testid="task-input"]')?.focus();
      }}
      on:showTimer={() => navigateToSection('timer')}
    />
    <div hidden={activeMobileTab !== 'tasks'}>
      <TaskPanel on:revealCapture={() => revealSection('tasks')} />
    </div>
    <div hidden={activeMobileTab !== 'plan'}>
      <PlanPanel
        on:openPlan={openNewPlan}
        on:editPlan={(event) => openPlanEditor(event.detail.plan)}
      />
    </div>
    <div hidden={activeMobileTab !== 'insights'}><InsightsPanel {now} /></div>
  </main>
  <footer class="workspace-footer">
    <span class={`sync-pill ${syncTone}`} aria-label="Sync status">{syncLabel}</span>
    <span class="save-status" aria-label="Save status">{saveLabel}</span>
  </footer>
  <nav class="mobile-tabbar" aria-label="Focus Flow sections">
    <div class="mobile-tabbar__tabs" role="tablist" aria-label="Focus Flow sections">
      <button
        id="mobile-tab-timer"
        role="tab"
        class:active={activeMobileTab === 'timer'}
        aria-selected={activeMobileTab === 'timer'}
        aria-controls="timer-tabpanel"
        on:click={() => navigateToSection('timer')}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="8" />
          <path d="M12 7v5l3 2" />
        </svg>
        <span>Timer</span>
      </button>
      <button
        id="mobile-tab-tasks"
        role="tab"
        class:active={activeMobileTab === 'tasks'}
        aria-selected={activeMobileTab === 'tasks'}
        aria-controls="tasks-tabpanel"
        on:click={() => navigateToSection('tasks')}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 6h12M8 12h12M8 18h12" />
          <path d="m3.8 6 1 1 2-2M3.8 12l1 1 2-2M3.8 18l1 1 2-2" />
        </svg>
        <span>Tasks</span>
      </button>
      <button
        id="mobile-tab-plan"
        role="tab"
        class:active={activeMobileTab === 'plan'}
        aria-selected={activeMobileTab === 'plan'}
        aria-controls="plan-tabpanel"
        on:click={() => navigateToSection('plan')}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M7 3v3M17 3v3M4.5 9.5h15M6.5 5.5h11a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-11a2 2 0 0 1-2-2v-10a2 2 0 0 1 2-2Z"
          />
          <path d="M8 13h4M8 16h7" />
        </svg>
        <span>Plan</span>
      </button>
      <button
        id="mobile-tab-insights"
        role="tab"
        class:active={activeMobileTab === 'insights'}
        aria-selected={activeMobileTab === 'insights'}
        aria-controls="insights-tabpanel"
        on:click={() => navigateToSection('insights')}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 16 10 11l3 3 6-7" />
          <path d="M15 7h4v4" />
        </svg>
        <span>Insights</span>
      </button>
    </div>
  </nav>
</div>

{#if presentation.kind === 'new-document' || presentation.kind === 'edit-plan-block'}
  <PlanModal plan={editingPlan} on:close={closePlanModal} />
{/if}

{#if presentation.kind === 'settings'}
  <SettingsModal on:close={closePresentation} />
{/if}
